import {
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { HashLink as Link } from "react-router-hash-link";

import video1 from "../../../assets/video/33bd06d6-cdec-463e-8b67-c70f630e35ae.MP4";

const ArrowRightIcon = () => (
	<svg
		viewBox="0 0 24 24"
		width="32"
		height="32"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M5 12h14M13 6l6 6-6 6" />
	</svg>
);

const LazyVideo = memo(function LazyVideo({ src, className }) {
	const containerRef = useRef(null);
	const videoRef = useRef(null);
	const [shouldLoad, setShouldLoad] = useState(false);

	useEffect(() => {
		const element = containerRef.current;
		if (!element) return undefined;

		if (!("IntersectionObserver" in window)) {
			setShouldLoad(true);
			return undefined;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setShouldLoad(true);
					observer.disconnect();
				}
			},
			{ rootMargin: "0px", threshold: 0.01 }
		);

		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (!shouldLoad || !videoRef.current) return;
		const playPromise = videoRef.current.play();
		if (playPromise && typeof playPromise.catch === "function") {
			playPromise.catch(() => {});
		}
	}, [shouldLoad]);

	return (
		<div ref={containerRef} className="absolute inset-0">
			{shouldLoad ? (
				<video
					ref={videoRef}
					src={src}
					autoPlay
					loop
					muted
					playsInline
					preload="none"
					aria-hidden="true"
					className={className}
				/>
			) : (
				<div className="absolute inset-0 bg-gray-200" />
			)}
		</div>
	);
});

function ExploreCollection() {
	const sectionRef = useRef(null);
	const fetchedRef = useRef(false);

	const initialStaticData = [
		{
			_id: "static-1",
			brand: "Prom Collection",
			media: [
				{
					url: video1,
					type: "video",
					filename: "prom-video.mp4",
				},
			],
			category: "prom",
		},
		{
			_id: "static-2",
			brand: "Evening Collection",
			media: [
				{
					url: "https://demo.riskydress.com/images/2026/ENCHANTED-E/E40237%20%281%29.png",
					type: "image",
					filename: "evening-dress.jpg",
				},
			],
			category: "evening",
		},
	];

	const [data, setData] = useState(initialStaticData);

	useEffect(() => {
		const target = sectionRef.current;
		if (!target) return undefined;

		let controller = null;
		let timeoutId = null;

		const fetchData = async () => {
			if (fetchedRef.current) return;
			fetchedRef.current = true;
			controller = new AbortController();
			timeoutId = window.setTimeout(() => controller?.abort(), 20000);

			try {
				const response = await fetch(
					"https://admin.monsinidress.com/api/hero-section/brand",
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ brand: "Monsini" }),
						signal: controller.signal,
					}
				);

				if (!response.ok) {
					throw new Error(`Hero request failed: ${response.status}`);
				}

				const result = await response.json();
				if (Array.isArray(result?.data) && result.data.length > 0) {
					const transformedData = result.data.map((item, index) => {
						const transformedMedia = Array.isArray(item.media)
							? item.media.map((mediaItem) => {
									const url = mediaItem?.url || "";
									return {
										...mediaItem,
										url: url.startsWith("http")
											? url
											: `https://admin.monsinidress.com/${url}`,
									};
							  })
							: [];

						return {
							_id: item._id || `api-${index}`,
							brand: item.brand || "Collection",
							media: transformedMedia,
							category: item.brand?.toLowerCase().includes("evening")
								? "evening"
								: "prom",
						};
					});

					setData(transformedData);
				}
			} catch (error) {
				// Static content intentionally remains visible if the optional API fails.
			} finally {
				if (timeoutId) window.clearTimeout(timeoutId);
			}
		};

		if (!("IntersectionObserver" in window)) {
			const fallbackTimer = window.setTimeout(fetchData, 8000);
			return () => {
				window.clearTimeout(fallbackTimer);
				controller?.abort();
				if (timeoutId) window.clearTimeout(timeoutId);
			};
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					fetchData();
					observer.disconnect();
				}
			},
			{ rootMargin: "0px", threshold: 0.05 }
		);

		observer.observe(target);
		return () => {
			observer.disconnect();
			controller?.abort();
			if (timeoutId) window.clearTimeout(timeoutId);
		};
	}, []);

	const renderMedia = useCallback((item) => {
		const media = item?.media?.[0];
		if (!media) {
			return (
				<div className="absolute inset-0 flex items-center justify-center bg-gray-200">
					<span className="text-gray-600">No media available</span>
				</div>
			);
		}

		if (String(media.type || "").toLowerCase() === "video") {
			return (
				<LazyVideo
					src={media.url}
					className="absolute inset-0 h-full w-full object-cover object-[center_39%]"
				/>
			);
		}

		return (
			<img
				src={media.url}
				alt={item.brand || "Collection"}
				loading="lazy"
				decoding="async"
				fetchPriority="high"
				width="900"
				height="1200"
				className="absolute inset-0 h-full w-full object-cover object-top"
			/>
		);
	}, []);

	return (
		<section
			ref={sectionRef}
			className="perf-defer-section flex flex-col items-center justify-center py-20"
		>
			<div className="mb-16 text-center">
				<h2 className="font-lora text-4xl font-medium md:text-5xl">
					Our Latest Collection
				</h2>
			</div>

			<div
				className={`grid w-full gap-6 px-4 md:px-10 lg:px-32 ${
					data.length === 1
						? "grid-cols-1 max-w-4xl"
						: "grid-cols-1 md:grid-cols-2"
				}`}
			>
				{data.map((item, index) => (
					<div
						key={item._id || index}
						className="relative h-[600px] w-full overflow-hidden rounded-2xl bg-gray-100 md:h-[700px] lg:h-[900px]"
					>
						{renderMedia(item)}
						<div
							className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"
							aria-hidden="true"
						/>

						<div className="absolute inset-0 flex items-end justify-start p-6 md:p-10">
							<Link
								smooth
								to="#collection"
								aria-label={`Explore ${item.brand || "collection"}`}
								className="flex cursor-pointer items-center justify-center rounded-full border border-white px-8 py-4 font-lora text-lg font-semibold tracking-wide text-white transition duration-300 hover:bg-gray-900"
							>
								EXPLORE COLLECTION
								<span className="ms-5 hidden md:block">
									<ArrowRightIcon />
								</span>
							</Link>
						</div>
					</div>
				))}
			</div>
		</section>
	);
}

export default memo(ExploreCollection);
